import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AdminGetProjectParams,
  AdminListProjectsParams,
  AdminCreateProjectInput,
  RequestParams,
  AdminSetProjectDefaultPhotoInput,
  AdminSetProjectMarketDetailsInput,
  AdminSetProjectPriceExprInput,
  AdminSetProjectPriorityInput,
  AdminGetProjectPriceExprParams,
  AdminUpdateProjectAgentLinkStatusInput,
  AdminListProjectAgentLinksParams,
  AdminGetProjectAgentLinksParams,
  AdminPrepareProjectDocumentUploadInput,
  AdminVerifyProjectDocumentUploadInput,
  AdminPrepareProjectLogoUploadInput,
  AdminVerifyProjectLogoUploadInput,
  AdminPrepareProjectPhotoUploadInput,
  AdminVerifyProjectPhotoUploadInput,
} from '../api'
import {
  adminGetProjectDetailsOptions,
  adminListProjectsOptions,
  adminGetProjectPriceExprOptions,
  adminListProjectAgentLinksOptions,
  adminGetProjectAgentLinksOptions,
} from './admin-project.options'
import projectService from './admin-project.service'
import { EAdminProjectQueryKey } from './admin-project.types'
import type { IMutationArgs } from '../types'

export const useAdminGetProjectDetails = (params: AdminGetProjectParams) => {
  return useQuery(adminGetProjectDetailsOptions(params))
}

export const useAdminListProjects = (params: AdminListProjectsParams) => {
  return useQuery(adminListProjectsOptions(params))
}

export const useAdminCreateProject = (args: AdminCreateProjectInput) => {
  return useMutation({
    mutationFn: (params: AdminCreateProjectInput) =>
      projectService.adminCreateProject(params),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, args],
  })
}

export const useAdminUploadProjectPhoto = (params: RequestParams) => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      projectService.adminUploadProjectPhoto(formData),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, params],
  })
}

export const useAdminUploadProjectLogo = (params: RequestParams) => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      projectService.adminUploadProjectLogo(formData),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, params],
  })
}

export const useAdminUploadProjectDocument = (params: RequestParams) => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      projectService.adminUploadProjectDocument(formData),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, params],
  })
}

export const useAdminPrepareProjectDocumentUpload = () => {
  return useMutation({
    mutationFn: (data: AdminPrepareProjectDocumentUploadInput) =>
      projectService.adminPrepareProjectDocumentUpload(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'prepare-document'],
  })
}

export const useAdminVerifyProjectDocumentUpload = () => {
  return useMutation({
    mutationFn: (data: AdminVerifyProjectDocumentUploadInput) =>
      projectService.adminVerifyProjectDocumentUpload(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'verify-document'],
  })
}

export const useAdminPrepareProjectLogoUpload = () => {
  return useMutation({
    mutationFn: (data: AdminPrepareProjectLogoUploadInput) =>
      projectService.adminPrepareProjectLogoUpload(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'prepare-logo'],
  })
}

export const useAdminVerifyProjectLogoUpload = () => {
  return useMutation({
    mutationFn: (data: AdminVerifyProjectLogoUploadInput) =>
      projectService.adminVerifyProjectLogoUpload(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'verify-logo'],
  })
}

export const useAdminPrepareProjectPhotoUpload = () => {
  return useMutation({
    mutationFn: (data: AdminPrepareProjectPhotoUploadInput) =>
      projectService.adminPrepareProjectPhotoUpload(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'prepare-photo'],
  })
}

export const useAdminVerifyProjectPhotoUpload = () => {
  return useMutation({
    mutationFn: (data: AdminVerifyProjectPhotoUploadInput) =>
      projectService.adminVerifyProjectPhotoUpload(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'verify-photo'],
  })
}

export const useAdminDeleteProjectDocument = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args

  return useMutation({
    mutationFn: ({
      projectId,
      documentId,
    }: {
      projectId: number
      documentId: number
    }) => projectService.adminDeleteProjectDocument(projectId, documentId),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'delete-document'],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAdminProjectQueryKey.ADMIN_PROJECT],
        })
      }
      return data
    },
  })
}

export const useAdminDeleteProjectPhoto = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args

  return useMutation({
    mutationFn: ({
      projectId,
      photoId,
    }: {
      projectId: number
      photoId: number
    }) => projectService.adminDeleteProjectPhoto(projectId, photoId),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'delete-photo'],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAdminProjectQueryKey.ADMIN_PROJECT],
        })
      }
      return data
    },
  })
}

export const useAdminSetProjectDefaultPhoto = () => {
  return useMutation({
    mutationFn: (data: AdminSetProjectDefaultPhotoInput) =>
      projectService.adminSetProjectDefaultPhoto(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_SET_PROJECT_DEFAULT_PHOTO],
  })
}

export const useAdminSetProjectMarketDetails = () => {
  return useMutation({
    mutationFn: (data: AdminSetProjectMarketDetailsInput) =>
      projectService.adminSetProjectMarketDetails(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT],
  })
}

export const useAdminSetProjectPriceExpr = () => {
  return useMutation({
    mutationFn: (data: AdminSetProjectPriceExprInput) =>
      projectService.adminSetProjectPriceExpr(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT],
  })
}

export const useAdminGetProjectPriceExpr = (
  params: AdminGetProjectPriceExprParams,
) => {
  return useQuery(adminGetProjectPriceExprOptions(params))
}

export const useAdminUpdateProjectAgentLinkStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AdminUpdateProjectAgentLinkStatusInput) =>
      projectService.adminUpdateProjectAgentLinkStatus(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_UPDATE_PROJECT_AGENT_LINK_STATUS],
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [EAdminProjectQueryKey.ADMIN_LIST_PROJECT_AGENT_LINKS],
      })
      return data
    },
  })
}

export const useAdminListProjectAgentLinks = (
  params: AdminListProjectAgentLinksParams,
) => {
  return useQuery(adminListProjectAgentLinksOptions(params))
}

export const useAdminGetProjectAgentLinks = (
  params: AdminGetProjectAgentLinksParams,
) => {
  return useQuery(adminGetProjectAgentLinksOptions(params))
}

export const useAdminSetProjectPriority = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args

  return useMutation({
    mutationFn: (data: AdminSetProjectPriorityInput) =>
      projectService.adminSetProjectPriority(data),
    mutationKey: [EAdminProjectQueryKey.ADMIN_PROJECT, 'set-priority'],
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAdminProjectQueryKey.ADMIN_PROJECT],
        })
      }
      return data
    },
  })
}
